import React, { useRef, useLayoutEffect } from "react";
import { gsap, prefersReducedMotion } from "../../lib/motion";

/**
 * Word-by-word mask reveal: each word rises out of an overflow-hidden mask with
 * a blur-to-sharp settle, staggered on scroll-in. Manages its own DOM inside an
 * empty ref element (React never fights the injected spans).
 *
 * Usage: <Reveal as="h2" className="...">Some heading text</Reveal>
 */
export const Reveal = ({
  as: Tag = "div",
  children,
  className = "",
  stagger = 0.05,
  duration = 1.05,
  y = 118,
  delay = 0,
  start = "top 88%",
  ...rest
}) => {
  const ref = useRef(null);
  const text = typeof children === "string" ? children : Array.isArray(children) ? children.join("") : "";

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !text) return;

    if (prefersReducedMotion()) {
      el.textContent = text;
      return;
    }

    el.textContent = "";
    const parts = text.split(/(\s+)/);
    const words = [];
    parts.forEach((p) => {
      if (p === "" ) return;
      if (/^\s+$/.test(p)) {
        el.appendChild(document.createTextNode(p));
        return;
      }
      const mask = document.createElement("span");
      mask.style.display = "inline-block";
      mask.style.overflow = "hidden";
      mask.style.verticalAlign = "top";
      mask.style.paddingBottom = "0.14em";
      mask.style.marginBottom = "-0.14em";
      const word = document.createElement("span");
      word.style.display = "inline-block";
      word.style.willChange = "transform, filter, opacity";
      word.textContent = p;
      mask.appendChild(word);
      el.appendChild(mask);
      words.push(word);
    });

    gsap.set(words, { yPercent: y, opacity: 0, filter: "blur(12px)" });
    const tween = gsap.to(words, {
      yPercent: 0,
      opacity: 1,
      filter: "blur(0px)",
      duration,
      delay,
      ease: "expo.out",
      stagger,
      scrollTrigger: { trigger: el, start },
    });

    return () => {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
      if (el) el.textContent = text;
    };
  }, [text, stagger, duration, y, delay, start]);

  return <Tag ref={ref} className={className} aria-label={text} {...rest} />;
};

export default Reveal;
